import React from 'react'
import { DashboardLayout } from '../../layouts'
import { Content } from '../Dashboard/components'
import { Block, H4, NonIdealState } from '@mote/ui'
import { useAuth } from '../../auth/useAuth'
import styled, { css } from 'styled-components'
import { Redirect, useHistory, useLocation } from 'react-router-dom'
import { getGodModeOptions, getGodModeURLSuffix } from '../../shared/Utils'

import { getMoteLogin, identifyMaybeAndBroadcast } from '../../shared/Auth'
import { AccountLoadingState } from './AccountLoadingState'
import { AccountRoutes } from './AccountRoutes'
import { useAccountQuery } from '@mote/api'

const NAV_BUTTONS = [
  {
    name: 'Profile',
    path: '/account/profile'
  },
  {
    name: 'My subscription',
    path: '/account'
  },
  {
    name: 'Manage Users',
    path: '/account/users'
  },
  {
    name: 'Notifications',
    path: '/account/notifications'
  },
  {
    name: 'Refer a friend',
    path: '/account/referrals'
  }
]

export const AccountRouter = () => {
  const [account, setAccount] = React.useState(null)
  const [showProfile, setShowProfile] = React.useState(false)
  const { mutate: queryAccount, error } = useAccountQuery()

  const { user, isAuthenticated, updateUser } = useAuth()
  const history = useHistory()
  const location = useLocation()

  React.useEffect(() => {
    fetchAccount()
  }, [])

  async function fetchAccount() {
    const godModeOptions = getGodModeOptions()
    const response = await queryAccount({
      variables: godModeOptions || {}
    })
    if (!response || response.error || !response.data) return

    setAccount(response.data.account)
    setShowProfile(response.data.identity.labs?.zidaneMode)
    if (!response.data.identity.labs?.zidaneMode) {
      NAV_BUTTONS.shift()
    }
    identifyMaybeAndBroadcast()
    updateUser(getMoteLogin())
  }

  function handleNavButtonClick(path) {
    history.push({ pathname: path, search: getGodModeURLSuffix() })
  }

  function handleAccountUpdated(newAccountData) {
    if (newAccountData) {
      setAccount(newAccountData)
      return
    }
    fetchAccount()
  }

  if (!isAuthenticated) return <Redirect to={'/'} />
  if (account === null && !error) return <AccountLoadingState />
  if (error) {
    return (
      <DashboardLayout>
        <NonIdealState height={'auto'}>
          <H4 fontWeight={'400'} fontSize={21} alignment='center'>
            We were not able to fetch your account. Please try again and if the
            problem persists kindly contact us at support@mote.com
          </H4>
        </NonIdealState>
      </DashboardLayout>
    )
  }

  return (
    <DashboardLayout>
      <Content title='Account Settings' gap={48}>
        <>
          <Buttons layout='horizontal' gap={24}>
            {!user.isMinor &&
              NAV_BUTTONS.map(button => {
                if (
                  button.path === '/account/users' &&
                  !account.isInstitutionalAdmin
                )
                  return null

                return (
                  <NavButton
                    key={button.name}
                    onClick={_ => handleNavButtonClick(button.path)}
                    active={location.pathname === button.path}
                  >
                    <span>{button.name}</span>
                  </NavButton>
                )
              })}
          </Buttons>
          <AccountRoutes
            account={account}
            showProfile={showProfile}
            onAccountUpdated={handleAccountUpdated}
          />
        </>
      </Content>
    </DashboardLayout>
  )
}

const Buttons = styled(Block)`
  @media (max-width: 639px) {
    overflow: hidden;
    overflow-x: auto;
    min-height: 50px;
    -webkit-overflow-scrolling: touch;
  }
`

const NavButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  padding: 0;
  font-size: 15px;
  line-height: 100%;
  height: 40px;
  color: rgba(37, 37, 45, 0.5);
  border-radius: 4px;
  transition: all 150ms ease-in;
  position: relative;
  appearance: none;
  user-select: none;

  &:hover {
    color: #25252d;
  }

  &:active {
    color: #ac0ae8;
  }

  &:focus {
    outline: 0;
  }

  span:after {
    content: '';
    position: absolute;
    width: 100%;
    height: 3px;
    bottom: 0;
    left: 0;
    background-color: #ac0ae8;
    transform: scaleX(0);
    transform-origin: bottom right;
    transition: transform 400ms cubic-bezier(0.86, 0, 0.07, 1);
  }

  ${({ active }) =>
    active &&
    css`
      color: #25252d;
      pointer-events: none;

      span:after {
        transform: scaleX(1);
        transform-origin: bottom left;
      }
    `};
`
